import React, { useEffect, useState } from 'react';

import useUpdateCategoryOrder from '../../../hooks/category/useUpdateCategoryOrder';

import { ICategory } from '../../../types/category';

import { SVGIcon } from '../../wrappers/SVGIcon';

import { Icons } from '../../../data/Assets';

interface ISidebarProps {
    currOption: number[];
    categories: ICategory[];
    setCategories: React.Dispatch<React.SetStateAction<ICategory[]>>;
    setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
    isResetNew: boolean;
    setIsResetNew: React.Dispatch<React.SetStateAction<boolean>>;
}

const Sidebar = ({ currOption, setCurrOption, categories, setCategories, isResetNew, setIsResetNew }: ISidebarProps) => {
    const [treeStack, setTreeStack] = useState<{ cat: number, prog: number, comp: number, prog_func: number, param: number }[]>([]);

    const [showNewCategory, setShowNewCategory] = useState<boolean>(false);
    const [showNewProgram, setShowNewProgram] = useState<boolean>(false);
    const [showNewComponent, setShowNewComponent] = useState<boolean>(false);
    const [showNewProgFunction, setShowNewProgFunction] = useState<boolean>(false);
    const [showNewParameter, setShowNewParameter] = useState<boolean>(false);

    const [showCategory, setShowCategory] = useState<Set<number>>(new Set());
    const [showProgram, setShowProgram] = useState<{ cat: number, prog: number }[]>([]);
    const [showComponent, setShowComponent] = useState<{ cat: number, prog: number, comp: number }[]>([]);
    const [showProgFunction, setShowProgFunction] = useState<{ cat: number, prog: number, comp: number, prog_func: number }[]>([]);

    const { mutate: updateCategoryOrder } = useUpdateCategoryOrder();

    const resetStack = () => {
        setCurrOption([categories.length]);
        setTreeStack([]);
        setShowCategory(new Set());
        setShowProgram([]);
        setShowComponent([]);
        setShowProgFunction([]);
    }

    useEffect(() => {
        if (isResetNew) {
            if (currOption.length === 1) {
                setTreeStack([]);
                setShowCategory(new Set());
                setShowProgram([]);
                setShowComponent([]);
                setShowProgFunction([]);
            }
            resetNewOptions();
            setIsResetNew(false);
        }
    }, [isResetNew]); // eslint-disable-line

    useEffect(() => {
        resetNewOptions();
    }, [categories]) //eslint-disable-line

    const resetNewOptions = () => {
        setShowNewCategory(false);
        setShowNewProgram(false);
        setShowNewComponent(false);
        setShowNewProgFunction(false);
        setShowNewParameter(false);
    }

    const isSelect = (checkArr: number[]) => {
        if (checkArr.length !== currOption.length) return '';
        for (let i = 0; i < checkArr.length; i++) {
            if (checkArr[i] !== currOption[i]) return '';
        }
        return 'selected';
    }

    const isProgramExpand = (catIdx: number, progIdx: number) => {
        const expandProgram = showProgram.filter(prog => prog.cat === catIdx && prog.prog === progIdx).length !== 0;
        return expandProgram;
    }

    const isComponentExpand = (catIdx: number, progIdx: number, compIdx: number) => {
        const expandComponent = showComponent.filter(comp => comp.cat === catIdx && comp.prog === progIdx && comp.comp === compIdx).length !== 0;
        return expandComponent;
    };

    const isProgFunctionExpand = (catIdx: number, progIdx: number, compIdx: number, progFuncIdx: number) => {
        const expandProgFunction = showProgFunction.filter(prog_func => prog_func.cat === catIdx && prog_func.prog === progIdx && prog_func.comp === compIdx && prog_func.prog_func === progFuncIdx).length !== 0;
        return expandProgFunction;
    };

    const expandOrContractCategory = (i: number) => {
        setCurrOption([i]);
        resetNewOptions();

        const hasCategory = treeStack.filter(stack => stack.cat === i).length;
        if (hasCategory) {
            setTreeStack((prev) => prev.filter(stack => stack.cat !== i));
            const delCategory = showCategory;
            delCategory.delete(i);
            setShowCategory(delCategory);
        } else {
            const oldCategory = [...showCategory, i];
            setShowCategory(new Set([...oldCategory]));
            setTreeStack([...treeStack, { cat: i, prog: -1, comp: -1, prog_func: -1, param: -1 }]);
        }
    }

    const expandOrContractProgram = (i: number, j: number) => {
        setCurrOption([i, j]);
        resetNewOptions();

        const hasPage = treeStack.filter(stack => stack.cat === i && stack.prog === j).length;
        if (hasPage) {
            setTreeStack((prev) => prev.filter(stack => !(stack.cat === i && stack.prog === j)));
            setShowProgram((prev) => prev.filter(prog => !(prog.cat === i && prog.prog === j)));
        } else {
            setShowProgram([...showProgram, { cat: i, prog: j }]);
            setTreeStack([...treeStack, { cat: i, prog: j, comp: -1, prog_func: -1, param: -1 }]);
        }
    }

    const expandOrContractComponent = (i: number, j: number, k: number) => {
        setCurrOption([i, j, k]);
        resetNewOptions();

        const hasQuestion = treeStack.filter(stack => stack.cat === i && stack.prog === j && stack.comp === k).length;
        if (hasQuestion) {
            setTreeStack((prev) => prev.filter(stack => !(stack.cat === i && stack.prog === j && stack.comp === k)));
            setShowComponent((prev) => prev.filter(comp => !(comp.cat === i && comp.prog === j && comp.comp === k)));
        } else {
            setShowComponent([...showComponent, { cat: i, prog: j, comp: k }]);
            setTreeStack([...treeStack, { cat: i, prog: j, comp: k, prog_func: -1, param: -1 }]);
        }
    }

    const expandOrContractProgramFunction = (i: number, j: number, k: number, l: number) => {
        setCurrOption([i, j, k, l]);
        resetNewOptions();

        const hasQuestion = treeStack.filter(stack => stack.cat === i && stack.prog === j && stack.comp === k && stack.prog_func === l).length;
        if (hasQuestion) {
            setTreeStack((prev) => prev.filter(stack => !(stack.cat === i && stack.prog === j && stack.comp === k && stack.prog_func === l)));
            setShowProgFunction((prev) => prev.filter(prog_func => !(prog_func.cat === i && prog_func.prog === j && prog_func.comp === k && prog_func.prog_func === l)));
        } else {
            setShowProgFunction([...showProgFunction, { cat: i, prog: j, comp: k, prog_func: l }]);
            setTreeStack([...treeStack, { cat: i, prog: j, comp: k, prog_func: l, param: -1 }]);
        }
    }

    const handleCreateCategoryNav = () => {
        // resetStack();
        setCurrOption([categories.length]);
        expandOrContractCategory(categories.length);
        setShowNewCategory(true);
    }

    const handleCreateProgramNav = (catIdx: number) => {
        setCurrOption([catIdx, categories[catIdx].programs.length]);
        expandOrContractProgram(catIdx, categories[catIdx].programs.length);
        setShowNewProgram(true);
    }

    const handleCreateComponentNav = (catIdx: number, progIdx: number) => {
        setCurrOption([catIdx, progIdx, categories[catIdx].programs[progIdx].components.length]);
        expandOrContractComponent(catIdx, progIdx, categories[catIdx].programs[progIdx].components.length);
        // setTreeStack([...treeStack, { cat: catIdx, prog: progIdx, comp: categories[catIdx].programs[progIdx].components.length, prog_func: -1, param: -1 }]);
        setShowNewComponent(true);
    }

    const handleCreateProgFunctionNav = (catIdx: number, progIdx: number, compIdx: number) => {
        setCurrOption([catIdx, progIdx, compIdx, categories[catIdx].programs[progIdx].components[compIdx].program_functions.length]);
        expandOrContractProgramFunction(catIdx, progIdx, compIdx, categories[catIdx].programs[progIdx].components[compIdx].program_functions.length);
        // setTreeStack([...treeStack, { cat: catIdx, prog: progIdx, comp: compIdx, prog_func: categories[catIdx].programs[progIdx].components[compIdx].program_functions.length, param: -1 }]);
        setShowNewProgFunction(true);
    }

    const handleCreateParameterNav = (catIdx: number, progIdx: number, compIdx: number, progFuncIdx: number) => {
        setCurrOption([catIdx, progIdx, compIdx, progFuncIdx, categories[catIdx].programs[progIdx].components[compIdx].program_functions[progFuncIdx].parameters.length ? categories[catIdx].programs[progIdx].components[compIdx].program_functions[progFuncIdx].parameters.length : categories[catIdx].programs[progIdx].components[compIdx].program_functions[progFuncIdx].parameters.length]);
        // setTreeStack([...treeStack, { cat: catIdx, prog: progIdx, comp: compIdx, prog_func: progFuncIdx, param: -1 }]);
        setShowNewParameter(true);
    }

    const isAddButtonDisabled = (type: string) => {
        const commonCondition = showNewCategory || showNewProgram || showNewComponent || showNewProgFunction || showNewParameter;

        switch (type) {
            case 'category':
                return commonCondition;
            case 'program':
                return !categories?.length || commonCondition || currOption.length < 1;
            case 'component':
                return !categories[currOption[0]]?.programs?.length || commonCondition || currOption.length < 2;
            case 'prog_function':
                return !categories[currOption[0]]?.programs[currOption[1]]?.components?.length || commonCondition || currOption.length < 3;
            case 'parameter':
                return !categories[currOption[0]]?.programs[currOption[1]]?.components[currOption[2]]?.program_functions?.length || commonCondition || currOption.length < 4;
            default:
                return false;
        }
    }

    const swapPosCategory = (x: number, y: number) => {
        updateCategoryOrder({ categoryId: categories[x].id ?? 0, action: x === y - 1 ? "down" : "up" });
        const newArr = categories;
        const temp = newArr[x];
        newArr[x] = newArr[y];
        newArr[y] = temp;
        setCurrOption([y]);
        setCategories([...newArr]);
    };

    const moveCategoryUp = () => {
        if (currOption[0] === 0) {
            // do not move up
            console.log("Cannot move up!");
        } else {
            resetStack();
            swapPosCategory(currOption[0], currOption[0] - 1);
        }
    };

    const moveCategoryDown = () => {
        if (currOption[0] === categories.length - 1) {
            // do not move down
            console.log("Cannot move down!");
        } else {
            resetStack();
            swapPosCategory(currOption[0], currOption[0] + 1);
        }
    };

    const movingUpButtonsDisableCondition = () => {
        return currOption[0] === categories.length || currOption[0] === 0 || showNewCategory || showNewProgram || showNewComponent || showNewProgFunction || showNewParameter || currOption.length > 1;
    };

    const movingDownButtonsDisableCondition = () => {
        return currOption[0] >= categories.length - 1 || showNewCategory || showNewProgram || showNewComponent || showNewProgFunction || showNewParameter || currOption.length > 1;
    };

    return (
        <div className='side_bar'>
            <div className='options'>
                <button disabled={isAddButtonDisabled('category')} onClick={handleCreateCategoryNav}><SVGIcon src={Icons.Plus} />Category</button>
                <button disabled={isAddButtonDisabled('program')} onClick={() => handleCreateProgramNav(currOption[0])}><SVGIcon src={Icons.Plus} />Program</button>
                <button disabled={isAddButtonDisabled('component')} onClick={() => handleCreateComponentNav(currOption[0], currOption[1])}><SVGIcon src={Icons.Plus} />Component</button>
                <button disabled={isAddButtonDisabled('prog_function')} onClick={() => handleCreateProgFunctionNav(currOption[0], currOption[1], currOption[2])}><SVGIcon src={Icons.Plus} />Function</button>
                <button disabled={isAddButtonDisabled('parameter')} onClick={() => handleCreateParameterNav(currOption[0], currOption[1], currOption[2], currOption[3])}><SVGIcon src={Icons.Plus} />Parameter</button>
            </div>
            <div className='options_tree'>
                {showNewCategory && <div id="new_category" className={`category cont selected`} onClick={() => setCurrOption([categories.length])}>
                    <SVGIcon src={Icons.TriangleFill} /> <p>New category</p>
                </div>}
                {categories.map((category, i) => (
                    <div key={i}>
                        <div className={`category cont ${isSelect([i])} ${showCategory.has(i) ? 'rotate_svg' : ''}`} onClick={() => expandOrContractCategory(i)}>
                            <SVGIcon src={Icons.TriangleFill} /> <p>{category.name}</p>
                        </div>

                        {showNewProgram && currOption[0] === i && <div id="new_program" className={`program cont selected`} onClick={() => setCurrOption([i, categories[i].programs.length])}>
                            <SVGIcon src={Icons.TriangleFill} /> <p>New Program</p>
                        </div>}
                        {showCategory.has(i) && category.programs.map((program, j) => (
                            <div key={j}>
                                <div className={`program cont ${isSelect([i, j])} ${isProgramExpand(i, j) ? 'rotate_svg' : ''}`} onClick={() => expandOrContractProgram(i, j)}>
                                    <SVGIcon src={Icons.TriangleFill} /> <p><span>[{program.method}]</span> {program.name}</p>
                                </div>

                                {showNewComponent && currOption[0] === i && currOption[1] === j && <div id="new_component" className={`component cont selected`} onClick={() => setCurrOption([i, j, categories[i].programs[j].components.length])}>
                                    <SVGIcon src={Icons.TriangleFill} /> <p>New Component</p>
                                </div>}
                                {isProgramExpand(i, j) && program.components.map((component, k) => (
                                    <div key={k}>
                                        <div className={`component cont ${isSelect([i, j, k])} ${isComponentExpand(i, j, k) ? 'rotate_svg' : ''}`} onClick={() => expandOrContractComponent(i, j, k)}>
                                            <SVGIcon src={Icons.TriangleFill} /> <p><span>[{component.method}]</span> {component.description.slice(0, 40) + (component.description.length > 40 ? '...' : '')}</p>
                                        </div>

                                        {showNewProgFunction && currOption[0] === i && currOption[1] === j && currOption[2] === k && <div id="new_prog_function" className={`program_function cont selected`} onClick={() => setCurrOption([i, j, k, categories[i].programs[j].components[k].program_functions.length])}>
                                            <SVGIcon src={Icons.TriangleFill} /> <p>New Program Function</p>
                                        </div>}
                                        {isComponentExpand(i, j, k) && component.program_functions.map((progFunc, l) => (
                                            <div key={l}>
                                                <div className={`program_function cont ${isSelect([i, j, k, l])} ${isProgFunctionExpand(i, j, k, l) ? 'rotate_svg' : ''}`} onClick={() => expandOrContractProgramFunction(i, j, k, l)}>
                                                    <SVGIcon src={Icons.TriangleFill} /> <p>{progFunc.name}</p>
                                                </div>

                                                {showNewParameter && currOption[0] === i && currOption[1] === j && currOption[2] === k && currOption[3] === l && <div id="new_parameter" className={`parameter cont selected`} onClick={() => setCurrOption([i, j, k, l, categories[i].programs[j].components[k].program_functions[l].parameters.length])}>
                                                    <p>New Parameter</p>
                                                </div>}
                                                {isProgFunctionExpand(i, j, k, l) && progFunc.parameters.map((param, m) => (
                                                    <div key={m} className={`parameter cont ${isSelect([i, j, k, l, m])}`} onClick={() => { setCurrOption([i, j, k, l, m]); resetNewOptions() }}>
                                                        <p>{param.name}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            <div className="options">
                <button
                    onClick={moveCategoryUp}
                    disabled={movingUpButtonsDisableCondition()}
                >
                    <SVGIcon src={Icons.ArrowShort} rotation={180} />
                    Move Up
                </button>
                <button
                    onClick={moveCategoryDown}
                    disabled={movingDownButtonsDisableCondition()}
                >
                    <SVGIcon src={Icons.ArrowShort} />
                    Move Down
                </button>
            </div>
        </div>
    );
}

export default Sidebar;